import React from 'react';
import { Box, CloseButton, Kbd, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useIsPhone } from '@/hooks/use.responsive';
import { useSlashFocus } from '@/hooks/use.slash.focus';
import FilterChips from './filters/Filter.Chips';
import FilterMenu from './filters/Filter.Menu';
import type { TypeDateSection, TypeFilterSection, TypeListSort } from '@/types/list/types.list.filter';

export type { TypeDateSection, TypeFilterOption, TypeFilterSection } from '@/types/list/types.list.filter';

type Props = {
    search: string;
    onSearch: (value: string) => void;
    placeholder: string;
    sections: TypeFilterSection[];
    active: Record<string, string[]>;
    onToggle: (sectionId: string, value: string) => void;
    excluded?: Record<string, boolean>;
    onExclude?: (sectionId: string, value: boolean) => void;
    onClear: () => void;
    dates?: TypeDateSection[];
    dateValues?: Record<string, string>;
    onDateChange?: (sectionId: string, value: string) => void;
    sort?: TypeListSort;
    sortChip?: boolean;
    shown?: number;
    total?: number;
    right?: React.ReactNode;
};

const ListToolbar: React.FC<Props> = ({
    search, onSearch, placeholder, sections, active, onToggle, excluded, onExclude, onClear,
    dates = [], dateValues = {}, onDateChange, sort, shown, total, right,
}) => {
    const isPhone = useIsPhone();
    const inputRef = React.useRef<HTMLInputElement>(null);
    useSlashFocus(inputRef);

    return (
        <Box mb="md">
            <Box style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--mantine-spacing-sm)' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 'var(--mantine-spacing-sm)', flex: '1 1 20rem', minWidth: 0 }}>
                    <TextInput
                        ref={inputRef}
                        value={search}
                        onChange={(event) => onSearch(event.currentTarget.value)}
                        onKeyDown={(event) => { if (event.key === 'Escape' && search) { event.preventDefault(); onSearch(''); } }}
                        placeholder={placeholder}
                        leftSection={<IconSearch size={16} stroke={1.8} />}
                        rightSection={search
                            ? <CloseButton size="sm" onClick={() => onSearch('')} aria-label="Svuota la ricerca" />
                            : isPhone ? null : <Kbd size="xs" aria-hidden>/</Kbd>}
                        rightSectionPointerEvents={search ? 'all' : 'none'}
                        aria-label={placeholder}
                        aria-keyshortcuts="/"
                        style={{ flex: 1, minWidth: 0 }}
                    />
                    <FilterMenu
                        sections={sections}
                        active={active}
                        onToggle={onToggle}
                        excluded={excluded}
                        onExclude={onExclude}
                        onClear={onClear}
                        dates={dates}
                        dateValues={dateValues}
                        onDateChange={onDateChange}
                        sort={sort}
                    />
                </Box>
                {right ? <Box style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '100%', marginLeft: 'auto' }}>{right}</Box> : null}
            </Box>

            <FilterChips
                sections={sections}
                active={active}
                onToggle={onToggle}
                excluded={excluded}
                onExclude={onExclude}
                onClear={onClear}
                dates={dates}
                dateValues={dateValues}
                onDateChange={onDateChange}
                sort={sort}
                shown={shown}
                total={total}
                searching={Boolean(search.trim())}
            />
        </Box>
    );
};

export default ListToolbar;
